import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Package, Search, PackagePlus, ArrowRightCircle, CheckCircle, Clock } from 'lucide-react';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Deliveries.module.css';

const Deliveries = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/security/deliveries');
      setDeliveries(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ company: '', agentName: '', type: 'Package', flatNumber: '', vehicleNumber: '', action: 'Keep at Gate' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        status: formData.action === 'Keep at Gate' ? 'Waiting' : 'Delivered'
      };
      const { data } = await api.post('/security/deliveries', payload);
      setDeliveries([data, ...deliveries]);
      setIsDrawerOpen(false);
      setFormData({ company: '', agentName: '', type: 'Package', flatNumber: '', vehicleNumber: '', action: 'Keep at Gate' });
      toast.success('Delivery logged successfully!');
    } catch (err) {
      toast.error('Failed to log delivery');
    }
  };

  const handleAction = async (id, status) => {
    try {
      const { data } = await api.put(`/security/deliveries/${id}/status`, { status });
      setDeliveries(deliveries.map(d => d._id === id ? data : d));
      toast.success('Status updated successfully!');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Delivery Management</h1>
          <p>Log incoming deliveries and manage packages kept at gate</p>
        </div>
        <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
          <PackagePlus size={16} /> New Delivery
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search flat or vendor..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Delivery ID</th>
                <th>Vendor Info</th>
                <th>Flat No.</th>
                <th>In Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="6">Loading...</td></tr> : deliveries.map(d => (
                <tr key={d._id}>
                  <td><strong>{d._id.substring(d._id.length - 6).toUpperCase()}</strong></td>
                  <td>
                    <div className={styles.vendorInfo}>
                      <Package size={16} className={styles.iconPkg} />
                      <div className={styles.details}>
                        <strong>{d.company}</strong>
                        <span>{d.type}{d.vehicleNumber ? ` • ${d.vehicleNumber}` : ''}</span>
                      </div>
                    </div>
                  </td>
                  <td><strong>{d.flatNumber || '-'}</strong></td>
                  <td>{d.entryTime ? new Date(d.entryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (d.createdAt ? new Date(d.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[d.status.replace(/\s+/g, '').toLowerCase()]}`}>
                      {d.status}
                    </span>
                  </td>
                  <td>
                    {d.status === 'Waiting' && (
                      <button className={styles.btnAction} onClick={() => handleAction(d._id, 'Delivered')}>
                        Mark Collected
                      </button>
                    )}
                    {d.status === 'Delivered' && (
                      <span className={styles.textMuted}>Done</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className={styles.mobileList}>
          {loading ? <p style={{padding: '16px'}}>Loading...</p> : deliveries.map(d => (
            <div key={d._id} className={styles.mobileCard}>
              <div className={styles.mobileCardHeader}>
                <div className={styles.vendorInfo}>
                  <Package size={16} className={styles.iconPkg} />
                  <div className={styles.details}>
                    <strong>{d.company}</strong>
                    <span>{d.type}{d.vehicleNumber ? ` • ${d.vehicleNumber}` : ''}</span>
                  </div>
                </div>
                <span className={`${styles.badgeStatus} ${styles[d.status.replace(/\s+/g, '').toLowerCase()]}`}>
                  {d.status}
                </span>
              </div>
              <div className={styles.mobileCardMeta}>
                <div className={styles.metaRow}>
                  <span>ID: <strong>{d._id.substring(d._id.length - 6).toUpperCase()}</strong></span>
                  <span>Flat: <strong>{d.flatNumber || '-'}</strong></span>
                </div>
                <div className={styles.metaRow}>
                  <span>In: {d.entryTime ? new Date(d.entryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (d.createdAt ? new Date(d.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</span>
                </div>
                <div className={styles.metaRow} style={{marginTop: '4px'}}>
                    {d.status === 'Waiting' && (
                      <button className={styles.btnAction} style={{width: '100%', justifyContent: 'center'}} onClick={() => handleAction(d._id, 'Delivered')}>
                        Mark Collected
                      </button>
                    )}
                    {d.status === 'Delivered' && (
                      <span className={styles.textMuted} style={{width: '100%', textAlign: 'center', padding: '6px 0'}}>Done</span>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Log Delivery">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Vendor / Company</label>
            <input name="company" value={formData.company} onChange={handleChange} required placeholder="e.g. Amazon, Zomato" />
          </div>
          <div className="formGroup">
            <label>Agent Name</label>
            <input name="agentName" value={formData.agentName} onChange={handleChange} required placeholder="Name of delivery agent" />
          </div>
          <div className="formGroup">
            <label>Vehicle Number (Optional)</label>
            <input name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} placeholder="e.g. MP-09-AB-1234" />
          </div>
          <div className="formGroup">
            <label>Package Type</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="Package">Courier / Package</option>
              <option value="Food">Food Delivery</option>
              <option value="Groceries">Groceries</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Delivery For (Flat)</label>
            <input name="flatNumber" value={formData.flatNumber} onChange={handleChange} required placeholder="e.g. A-101" />
          </div>
          <div className="formGroup">
            <label>Delivery Action</label>
            <select name="action" value={formData.action} onChange={handleChange}>
              <option value="Keep at Gate">Keep at Gate (Resident will collect)</option>
              <option value="Send Inside">Send Inside (Delivery person goes to flat)</option>
            </select>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Log Delivery</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Deliveries;
